import { memo } from 'react';
import { A4_PAGE_WIDTH } from '@/constants';
import { SpellcheckContext } from '@/context/SpellcheckContext';
import { useReportSections } from '@/hooks/use-report-sections';
import { SECTIONS } from '../wizard/constants';
import { MeasureWrapper } from './MeasureWrapper';
import {
    REPORT_FOOTER_MEASUREMENT_ID,
    REPORT_HEADER_MEASUREMENT_ID,
    REPORT_PATIENT_INFO_MEASUREMENT_ID,
    REPORT_SIGNATURE_MEASUREMENT_ID,
} from './pagination/constants';
import { PatientInfoTable } from './PatientInfoTable';
import PreviewPsychologistSignature from './PreviewPsychologistSignature';
import { ReportFooter } from './ReportFooter';
import { ReportFootnoteMeasurer } from './ReportFootnotes';
import { ReportHeader } from './ReportHeader';
import { ReportSection } from './ReportSection';
import SectionContent from './SectionContent';
import SectionTitle from './SectionTitle';
import type { ReportFootnotePlan } from './footnotes';
import type { Psychologist, ReportData } from './types';

interface HiddenReportMeasurerProps {
    data: ReportData;
    disabled: boolean;
    psychologist: Psychologist | null;
    footnotePlan: ReportFootnotePlan;
}

function HiddenReportMeasurerComponent({
    data,
    disabled,
    psychologist,
    footnotePlan,
}: HiddenReportMeasurerProps) {
    const sections = useReportSections(data);
    const footerMeasureStyle = {
        marginLeft: '-3rem',
        marginRight: '-3rem',
        width: A4_PAGE_WIDTH,
    };

    return (
        <SpellcheckContext.Provider value={{ enabled: false }}>
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: A4_PAGE_WIDTH,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    zIndex: -1000,
                    padding: '3rem', // Match ReportPreview padding (p-12 = 3rem)
                }}
                className="report-measurement-container"
            >
                <MeasureWrapper id={REPORT_HEADER_MEASUREMENT_ID}>
                    <ReportHeader data={data} psychologist={psychologist} />
                </MeasureWrapper>

                <MeasureWrapper id={REPORT_PATIENT_INFO_MEASUREMENT_ID}>
                    <PatientInfoTable
                        data={data}
                        onUpdate={() => {}}
                        disabled={disabled}
                        psychologist={psychologist}
                        idPrefix="hidden-"
                    />
                </MeasureWrapper>

                <MeasureWrapper id={REPORT_FOOTER_MEASUREMENT_ID} style={footerMeasureStyle}>
                    <ReportFooter
                        data={data}
                        currentPage={1}
                        totalPages={2}
                        psychologist={psychologist}
                    />
                </MeasureWrapper>

                <ReportFootnoteMeasurer footnotePlan={footnotePlan} />

                {sections.map((section) => {
                    if (section.id === SECTIONS.TESTS) {
                        // Special handling for Tests section to measure blocks individually
                        // We split the rendering into Title (measured) and Content (passed measureBlocks=true)
                        return (
                            <div key={section.id} className="mb-8">
                                <MeasureWrapper id={`section-${section.id}-title`}>
                                    <SectionTitle
                                        section={section}
                                        data={data}
                                        onUpdate={() => {}}
                                        disabled={disabled}
                                    />
                                </MeasureWrapper>

                                {/* SectionContent for 'tests' calls TestSectionRenderer */}
                                {/* We enable measureBlocks so individual blocks get measured inside */}
                                <SectionContent
                                    section={section}
                                    data={data}
                                    onUpdate={() => {}}
                                    disabled={disabled}
                                    onAddTest={() => {}}
                                    onAddChartFromTable={() => {}}
                                    onEditChart={() => {}}
                                    handleDeleteRequest={() => {}}
                                    measureBlocks={true}
                                    enableDragAndDrop={false}
                                    footnotePlan={footnotePlan}
                                />
                            </div>
                        );
                    }

                    return (
                        <div key={section.id} className="mb-8">
                            <MeasureWrapper id={`section-${section.id}-title`}>
                                <SectionTitle
                                    section={section}
                                    data={data}
                                    onUpdate={() => {}}
                                    disabled={disabled}
                                />
                            </MeasureWrapper>

                            <MeasureWrapper id={`section-${section.id}`}>
                                <ReportSection
                                    section={section}
                                    data={data}
                                    onUpdate={() => {}}
                                    disabled={disabled}
                                    handleDeleteRequest={() => {}}
                                    onAddTest={() => {}}
                                    onAddChartFromTable={() => {}}
                                    onEditChart={() => {}}
                                    idPrefix="hidden-"
                                />
                            </MeasureWrapper>
                        </div>
                    );
                })}

                <MeasureWrapper id={REPORT_SIGNATURE_MEASUREMENT_ID}>
                    <PreviewPsychologistSignature
                        full_name={psychologist?.full_name}
                        crp={psychologist?.crp}
                        city={psychologist?.city}
                        state={psychologist?.state}
                    />
                </MeasureWrapper>
            </div>
        </SpellcheckContext.Provider>
    );
}

export const HiddenReportMeasurer = memo(HiddenReportMeasurerComponent);
